<template>
  <div>
    <h2 class="content-heading">
      <button @click="printInvoices" class="btn btn-sm btn-success float-right" :disabled="printing">
        <i v-if="printing" class="fa fa-spin fa-spinner mr-5"></i>
        <i v-else class="fa fa-print mr-5"></i>
        Print pakbonnen
      </button>

      <a :href="invoicesPdfUrl" target="_blank" class="btn btn-sm btn-secondary mr-2 float-right" :disabled="printing">
        <i class="fa fa-file-pdf-o text-danger mr-5"></i> Download pakbonnen
      </a>

      <i class="si si-directions mr-5"></i> Bestellingen Picken
    </h2>

    <div v-if="loading" class="alert alert-success" role="alert">
      <i class="fa fa-spin fa-spinner"></i>&nbsp;&nbsp;Bestellingen laden...
    </div>

    <div v-else>
      <div v-if="!singleBin" class="row gutters-tiny">
        <div class="col-md-6 col-xl-2" v-for="order in orders" :key="order.id">

          <div class="block text-center bin" @click="detailModalOrderId = order.id">
            <div class="bin__key">{{order.bin.key}}</div>

            <div class="block-content block-content-full text-right" :style="{'background-color': '#' + order.bin.color}">
              <div class="font-w600 text-white mb-5">#{{order.number}}</div>
              <div class="text-white-op text-one-line" :title="order.billing_person">{{order.billing_person}}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="block mt-20">
        <div class="block-header block-header-default">
          <div class="block-title">
            <strong>{{this.currentProductIndex + 1}}</strong> van <strong>{{products.length}}</strong> producten
          </div>
          <div class="block-options">
            <button type="button" class="btn-block-option" @click="previous">
              <i class="si si-arrow-left"></i>
            </button>
            <button type="button" class="btn-block-option" @click="next">
              <i class="si si-arrow-right"></i>
            </button>
          </div>
        </div>
        <div class="block-content p-20">
          <div class="row">
            <div class="col-5">
              <swiper ref="imageSwiper" style="height: 450px;">
                <swiper-slide v-for="image in currentProduct.image_urls" :key="image">
                  <img :src="image" class="img-fluid">
                </swiper-slide>
              </swiper>
            </div>
            <div class="col-7">
              <h1 class="font-w700" @click="detailModalProductId = currentProduct.id">
                {{currentProduct.name}}
                <small class="product-info-button"><i class="si si-info"></i></small>
              </h1>
              <h2 class="mb-3">
                <mark class="pl-10 pr-10" v-if="currentProduct.location">{{currentProduct.location}}</mark>
                <mark class="pl-10 pr-10 text-muted" v-else>Locatie onbekend</mark>
                <span v-if="currentProduct.shares_location" class="stock-warning text-danger">
                  <strong>Let op:</strong> er liggen meedere producten op deze locatie!
                </span>
              </h2>
              <div>
                <span v-if="currentProduct.overstock_location" class="badge badge-secondary">Overvoorraad: {{ currentProduct.overstock_location }}</span> 
                <span v-if="currentProduct.in_store" class="badge badge-secondary">Ligt in de winkel</span>
              </div>

              <h4 class="mt-50">Aantal: {{totalQuantity}}</h4>
              <div v-if="singleBin" class="block text-center" style="background-color: #e82b18; width: 100px;">
                <div class="block-content">
                  <p class="font-size-h1 text-white">
                    <strong>{{totalQuantity}}x</strong>
                  </p>
                </div>
              </div>
              <div v-else class="row gutters-tiny">
                <div class="col-6 col-md-4 col-xl-2" v-for="(quantity, orderNumber) in currentProduct.quantities" :key="orderNumber">
                  <div class="block text-center" :style="{'background-color': '#' + findOrderByNumber(orderNumber).bin.color}">
                    <div class="block-content bg-black-op-5">
                      <p class="font-w600 text-white-op">#{{orderNumber}}</p>
                    </div>
                    <div class="block-content">
                      <p class="font-size-h1 text-white">
                        <strong>{{quantity}}x</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <nav class="clearfix push">
        <button type="button" class="btn btn-alt-secondary min-width-100 float-left" @click="previous" v-if="!isFirstProduct"><i class="fa fa-arrow-left"></i> Vorige</button>

        <button type="button" class="btn btn-success btn-noborder min-width-100 float-right btn-lg" @click="finish" v-if="isLastProduct"><i class="fa fa-check fa-fw"></i> Afronden</button>
        <button type="button" class="btn btn-alt-secondary min-width-100 float-right btn-lg" @click="next" v-else>Volgende <i class="fa fa-arrow-right fa-fw"></i></button>
      </nav>

      <product-detail-modal v-if="detailModalProductId" :key="detailModalProductId" :product-id="detailModalProductId" @close="detailModalProductId = null" />
      <order-detail-modal v-if="detailModalOrderId" :key="detailModalOrderId" :order-id="detailModalOrderId" @close="detailModalOrderId = null" />
    </div>
  </div>
</template>

<script>
import http from '../utils/http'
import { swiper, swiperSlide } from 'vue-awesome-swiper'
import { sum, values, find } from 'lodash'
import ProductDetailModal from '../products/ProductDetailModal'
import OrderDetailModal from './OrderDetailModal'

export default {
  components: {
    swiper,
    swiperSlide,
    ProductDetailModal,
    OrderDetailModal,
  },
  data () {
    return {
      orders: [],
      products: [],
      currentProductIndex: 0,
      showModal: false,
      loading: false,
      printing: false,
      detailModalOrderId: null,
      detailModalProductId: null
    }
  },
  computed: {
    currentProduct () {
      return this.products[this.currentProductIndex] || {}
    },
    isFirstProduct () {
      return this.currentProductIndex === 0
    },
    isLastProduct () {
      return this.currentProductIndex + 1 === this.products.length
    },
    swiper () {
      return this.$refs.imageSwiper.swiper
    },
    totalQuantity () {
      return sum(values(this.currentProduct.quantities))
    },
    orderNumbers () {
      return this.$route.query.orders
    },
    singleBin () {
      return this.$route.query.singleBin
    },
    invoicesPdfUrl () {
      return this.$http.getUri({
        url: process.env.API_URL + '/invoices/pdf',
        params: {
          orders: this.orderNumbers,
          access_token: this.$auth.getAccessToken()
        }
      })
    },
    lastPickForProduct () {
      return this.currentProduct.unpicked_quantity === this.totalQuantity
    }
  },
  methods: {
    next () {
      const i = this.currentProductIndex + 1
      if (i < this.products.length) {
        this.currentProductIndex = i
        this.swiper.slideTo(0)
      }
    },
    previous () {
      const i = this.currentProductIndex - 1
      if (i >= 0) {
        this.currentProductIndex = i
        this.swiper.slideTo(0)
      }
    },
    async finish () {
      const yes = await this.$swal({
        title: 'Afronden',
        text: 'Heb je alle producten gepickt?',
        icon: 'info',
        buttons: {
          cancel: {
            text: 'Nee, terug',
            visible: true
          },
          confirm: {
            text: 'Ja, afronden',
            className: 'swal-button--success'
          }
        }
      })

      if (yes) {
        this.$api.changeOrderStatus(this.orderNumbers, 'picked')
          .then(async () => {
            await this.$swal('Afgerond 😀', 'Succes met inpakken!', 'success')
            this.$router.push({ name: 'orders' })
          })
          .catch(() => {
            this.$swal('Oeps! 😳', 'Er is iets fout gegaan bij het afronden. Probeer het opnieuw.', 'error')
          })
      }
    },
    findOrderByNumber (orderNumber) {
      return find(this.orders, o => o.number === orderNumber)
    },
    printInvoices () {
      this.printing = true

      this.$api.printInvoices(this.orderNumbers)
        .then(() => {
          this.printing = false
        })
        .catch(() => {
          this.printing = false
          this.$swal('Oeps! 😳', `Er is iets fout gegaan bij het printen van de pakbonnen. Probeer het opnieuw.`, 'error')
        })
    }
  },
  created () {
    this.loading = true

    const params = {
      numbers: this.orderNumbers,
      single_bin: this.singleBin
    }

    http.get('/orders/pick', { params })
      .then((response) => {
        this.orders = response.data.data.orders
        this.products = response.data.data.products
        this.loading = false
      })
      .catch(async (error) => {
        if (error.response && error.response.data && error.response.data.message) {
          await this.$swal('Oeps! 😳', error.response.data.message, 'error')
        } else {
          await this.$swal('Oeps! 😳', 'Er is iets fout gegaan bij het ophalen van de bestellingen. Probeer het opnieuw.', 'error')
        }

        this.$router.push({ name: 'orders' })
      })
  }
}
</script>

<style scoped>
.bin {
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.bin__key {
  position: absolute;
  left: 2px;
  font-size: 120px;
  line-height: 70px;
  color: #fff;
  opacity: 0.2;
  font-weight: bold;
  letter-spacing: -10px;
}

.stock-warning {
  font-size: 15px;
  transform: translateY(-5px);
  display: inline-block;
}
.stock-warning strong {
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// PickOrdersPage.vue?4bc19912